"use client";

import { useMemo } from "react";
import { useRouter } from "next/navigation";
import { useTable } from "spacetimedb/react";
import { toast } from "sonner";
import { tables } from "@/module_bindings";
import { useRowTransitions } from "@/hooks/spacetime/use-row-transitions";
import { startNavigationFeedback } from "@/lib/navigation-feedback";
import type { Organization } from "@/types/organization";
import type { OrgMemberRow } from "@/lib/stdb-map";

/**
 * New org_members row appearing with status "requested" (create_join_request
 * always inserts — a withdrawn-and-retried request is a fresh row, never a
 * status flip), shown to admin viewers only. Uses onAppear rather than
 * onTransition because creation IS the event here; the hook's per-scope
 * seen-set keeps reconnect refills from replaying old requests.
 */
export function JoinRequestToasts({
  organization,
  enabled,
}: {
  organization: Organization | null;
  enabled: boolean;
}) {
  const router = useRouter();
  const [rows, isReady] = useTable(tables.org_members);
  const orgRows = useMemo(
    () =>
      organization
        ? rows.filter(
            (row: OrgMemberRow) => row.organizationId === organization.id,
          )
        : [],
    [rows, organization],
  );

  useRowTransitions<OrgMemberRow>({
    rows: orgRows,
    isReady,
    getId: (row) => row.id,
    getState: (row) => row.status,
    resetKey: enabled ? (organization?.id ?? null) : null,
    onAppear: (row) => {
      if (row.status !== "requested") return;
      toast.info("New Join Request", {
        id: `join-request-${row.id}`,
        description: "Someone requested to join your org.",
        action: {
          label: "Review",
          onClick: () => {
            startNavigationFeedback("/settings/members");
            router.push("/settings/members");
          },
        },
      });
    },
  });

  return null;
}
